import JSX, {Component, h} from "preact";
import axios from 'axios'
import {Configuration, Field, Provider} from "./types";
import {isFunction} from "../../libs/preact-entry";
import Header from "../header";
import Panel from "../panel";
import ButtonControl from "../button-control";
import globals from "../../utils/globals";
import TestModeLogsModal from "./TestModeLogsModal";
import classNames from "classnames";
import Switcher from "../control/Switcher";

type Log = {
  id: number,
  timestamp: string,
  message: string
}

interface Props {
  provider: Provider,
  onMount: () => Promise<any>,
  onSubmit: (configuration: Configuration) => void,
  children: JSX.Element,
  toggleProviderVisibility: (provider: Provider) => void
}

interface State {
  configuration: Configuration,
  errors: {
    [item: string]: any
  },
  logs: Array<Log>,
  isTestModeLogsModalOpen: boolean
}

export type ProvidedItems = {
  configuration: Configuration,
  onSubmit: (configuration: Configuration) => void,
  getError: (fieldName: string) => string | null,
  getSubmitButtonProps: () => {},
  onInputChange: (name: string, value: string) => void,
  onCheckboxChange: (name: string, value: boolean) => void
}

export default class AbstractProvider extends Component<Props, State> {
  private polling: number | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      configuration: this.getEmptyConfiguration(),
      errors: {},
      logs: [],
      isTestModeLogsModalOpen: false
    }

    this.openTestModeLogsModal = this.openTestModeLogsModal.bind(this)
    this.getHeaderProps = this.getHeaderProps.bind(this)
  }

  getEmptyConfiguration() {
    const {fields} = this.props.provider
    const emptyConfiguration: Configuration = {}

    fields.forEach((field: Field) => {
      emptyConfiguration[field.name] = ''
    })

    return emptyConfiguration
  }

  componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any): void {
    const {errors} = nextProps.provider
    this.setState({errors})
  }

  componentDidMount(): void {
    const { isInstalled } = this.props.provider
    if (isInstalled) {
      this.props.onMount().then(() => {
        const {configuration, errors} = this.props.provider
        this.setState({configuration, errors})
      })
    }
  }

  async onSubmit(e: Event): Promise<void> {
    e.preventDefault()
    if (this.validateForm()) {
      const {configuration} = this.state
      this.props.onSubmit(configuration)
    }
  }

  onTestMode (): void {
    const { provider: { name, isEnabled } } = this.props

    if(isEnabled) return

    axios.post(`/integration/sso/toggleTestMode`,  { provider: name })
        .then(() => {
          const loginUrl = `${globals('communityUrl')}/ssoproxy/login?ssoType=${name}`
          window.open(loginUrl, '_blank', 'noreferrer')

          this.openTestModeLogsModal()
        })
        .catch(e => {
          throw e
        })
  }

  openTestModeLogsModal (): void {
    const { isEnabled } = this.props.provider

    if (isEnabled) return

    this.fetchTestLogs().then(() => {
      const el = document.querySelector('.logs-container')
      if (el) el.scrollTop = el.scrollHeight;

      this.polling = setInterval(() => {
        this.fetchTestLogs()
      }, 3000)
    })

    this.setState({ isTestModeLogsModalOpen: true })
  }

  closeTestModeLogsModal (): void {
    if (this.polling) clearInterval(this.polling)

    this.setState({ isTestModeLogsModalOpen: false })
  }

  async fetchTestLogs() {
    const { provider: { name } } = this.props
    try {
      const response = await axios.get(`/integration/sso/testModeLogs?provider=${name}`)
      this.setState({ logs: response.data })
    } catch (e) {
      throw e
    }
  }

  onInputChange(name: string, value: string): void {
    const {configuration, errors} = this.state
    const newConfiguration = {...configuration, ...{[name]: value}}
    const isRequired = this.props.provider.fields.filter(field => field.name === name)[0].required
    if (isRequired) {
      const inputIsEmpty = !value.trim()
      if (inputIsEmpty) {
        this.setState({
          configuration: newConfiguration,
          errors: Object.assign(errors, {[name]: 'This is a required field'})
        })
        return
      }
    }
    delete errors![name]
    this.setState({
      configuration: newConfiguration,
      errors
    })
  }

  onCheckboxChange(name: string, value: boolean): void {
    const {configuration, errors} = this.state
    const newConfiguration = {...configuration, ...{[name]: value}}
    this.setState({
      configuration: newConfiguration,
      errors
    })
  }

  validateForm(): boolean {
    let errors = {}
    const {configuration} = this.state
    Object.keys(configuration).forEach(item => {
      const formField = configuration[item]
      if (typeof formField === "boolean"){
        return
      }
      const isRequired = this.props.provider.fields.filter(field => field.name === item)[0].required
      if(!isRequired) return
      const value = formField.trim()
      if (!value) Object.assign(errors, {[item]: 'This is a required field'})
    })
    if (Object.keys(errors).length > 0) {
      this.setState({errors})
      return false
    }
    return true
  }

  getSubmitButtonProps(): {} {
    const {isInstalled} = this.props.provider
    return {
      type: 'submit',
      title: isInstalled ? 'Save changes' : 'Install',
      variant: isInstalled ? 'success' : 'primary'
    }
  }

  getHeaderProps() {
    const { provider, toggleProviderVisibility } = this.props
    const { label, isEnabled, isInstalled } = provider

    const headerProps = {
      title: label,
      disableCancelButton: true,
      disableSaveButton: true,
      hasBreadcrumb: true,
      breadcrumbText: 'Back to SSO overview',
      breadcrumbLink: '/integration/sso',
    }

    if (isInstalled) Object.assign(headerProps, {
      statusLabelText: isEnabled ? 'Enabled' : isInstalled ? 'Installed' : '',
      statusLabelSuccess: isEnabled,
      statusLabelSecondary: !(isInstalled && isEnabled),
      customComponent: <div class='visibility-switch'>
        <Switcher on={isEnabled} onChange={() => toggleProviderVisibility(provider)}/>
        <span>Enable for end users</span>
      </div>
    })

    return headerProps
  }

  getError(fieldName: string): string | null {
    const {errors} = this.state
    if (Object.keys(errors).length === 0) return null
    const errorField = Object.keys(errors).filter((error: string) => error === fieldName)[0]
    return errors[errorField]
  }

  isReadyToRender(): boolean {
    const {isInstalled} = this.props.provider

    if (!isInstalled) return true

    const {configuration} = this.state
    const isConfigurationEmpty = Object.keys(configuration).filter(item => configuration[item]).length > 0

    return isInstalled && isConfigurationEmpty
  }

  render({children, provider, toggleProviderVisibility}: Props, { configuration, isTestModeLogsModalOpen, logs }: State) {
    if (!this.isReadyToRender()) return null
    if (isFunction(children[0])) {
      const { name, label, isEnabled, isInstalled } = provider
      const isTestLogInAvailable = !isEnabled && isInstalled

      return <div class={`provider-setup ${name}-setup`}>
        <Header {...this.getHeaderProps()} />
        <Panel title={`${label} setup`} customClass='sso-settings'>
          { children[0]({
            configuration: configuration,
            onSubmit: this.onSubmit.bind(this),
            getError: this.getError.bind(this),
            getSubmitButtonProps: this.getSubmitButtonProps.bind(this),
            onInputChange: this.onInputChange.bind(this),
            onCheckboxChange: this.onCheckboxChange.bind(this)
          }) }
        </Panel>
        <Panel title='Test log in' customClass='sso-settings'>
          <p>You can only test SSO and view logs if the method is disabled for end users. This lets you see a clean set
            of logs from your own internal testing.</p>
          <div>
            <ButtonControl title='Test log in' variant='primary' onClick={this.onTestMode.bind(this)}
                           disabled={!isTestLogInAvailable}/>
            <a className={classNames({'disabled': !isTestLogInAvailable})} onClick={this.openTestModeLogsModal}
               disabled={!isTestLogInAvailable}>View test logs</a>
          </div>
        </Panel>
        <TestModeLogsModal isOpen={isTestModeLogsModalOpen} logs={logs} onClose={() => this.closeTestModeLogsModal() } />
      </div>
    }
    return null
  }
}
